import appModeService from 'services/AppModeService';
import AzureAuthAdapter from 'services/adapters/AzureAuthAdapter';
import ParentPortalAuthAdapter from 'services/adapters/ParentPortalAuthAdapter';

/**
 * Unified Authentication Service
 * 
 * Provides a single interface for authentication regardless of mode.
 * Automatically routes to the appropriate adapter (Azure AD or Parent Portal).
 */
class AuthenticationService {
  constructor() {
    this.azureAdapter = new AzureAuthAdapter();
    this.parentPortalAdapter = new ParentPortalAuthAdapter();
  }

  /**
   * Get the appropriate adapter based on current mode
   * @returns {BaseAuthAdapter}
   */
  getAdapter() {
    return appModeService.isEmbedded ? this.parentPortalAdapter : this.azureAdapter;
  }

  /**
   * Validate if the user is authenticated
   * @param {boolean} forceRefresh - Force refresh the validation
   * @returns {Promise<{isValidUser: boolean, userInfo?: object, token?: string, isEmbedded?: boolean}>}
   */
  async validateUser(forceRefresh = false) {
    const adapter = this.getAdapter();
    return adapter.validateUser(forceRefresh);
  }

  /**
   * Get the current authentication token
   * @returns {string | null}
   */
  getToken() {
    const adapter = this.getAdapter();
    return adapter.getToken();
  }

  /**
   * Initiate login flow
   * @param {object} options - Login options (e.g., { expirationDate: Date })
   * @returns {Promise<void>}
   * @throws {Error} If login is not available (e.g., in embedded mode)
   */
  async login(options = {}) {
    const adapter = this.getAdapter();
    return adapter.login(options);
  }

  /**
   * Handle logout
   * @returns {Promise<void>}
   */
  async logout() {
    const adapter = this.getAdapter();
    return adapter.logout();
  }

  /**
   * Get user information
   * @returns {Promise<object | null>}
   */
  async getUserInfo() {
    const adapter = this.getAdapter();
    return adapter.getUserInfo();
  }

  /**
   * Check if user is authenticated (synchronous)
   * Note: May not be accurate for Azure AD, use validateUser() for reliable check
   * @returns {boolean}
   */
  isAuthenticated() {
    const adapter = this.getAdapter();
    return adapter.isAuthenticated();
  }

  /**
   * Refresh the authentication token
   * @returns {Promise<string | null>}
   */
  async refreshToken() {
    const adapter = this.getAdapter();
    return adapter.refreshToken();
  }

  /**
   * Check if currently in embedded mode
   * @returns {boolean}
   */
  isEmbeddedMode() {
    return appModeService.isEmbedded;
  }

  /**
   * Check if currently in standalone mode
   * @returns {boolean}
   */
  isStandaloneMode() {
    return appModeService.isStandalone;
  }
}

// Create singleton instance
const authenticationService = new AuthenticationService();

export default authenticationService;

